// src/pages/UserFleet.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Rocket, Upload, Search, Grid3x3, List, DollarSign, Package, Users, BookOpen, Trash2, RefreshCw, Flag } from 'lucide-react';
import api from '../api/axios';
import styles from '../styles/UserFleet.module.css';
import tficLogo from '../assets/Icons/NEWTFICLOGO_no_bg.png';

interface UserFleetShip {
  id: number;
  userId: number;
  shipId: number;
  shipName: string;
  manufacturer: string;
  variant: string;
  role: string;
  status: 'owned' | 'pledged' | 'planned' | 'rented' | 'loaned';
  acquisitionMethod: 'pledge' | 'in-game' | 'upgrade' | 'gift' | 'rental' | 'loaned';
  acquisitionDate?: string;
  notes?: string;
  isFavorite: boolean;
  imageUrl?: string;
  pledgeId?: string;
  pledgeName?: string;
  value?: number;
  cargoCapacity?: number;
  crew?: number;
  // Org Fleet pledge (see docs/org_fleet_v1_plan.md)
  pledgedToOrg?: boolean;
  pledgedAt?: string;
  orgPledgeNote?: string;
  orgPledgeRole?: string;
}

const PLEDGE_ELIGIBLE_STATUSES = new Set<UserFleetShip['status']>(['owned', 'pledged']);

interface FleetStats {
  totalShips: number;
  totalValue: number;
  totalCargo: number;
  totalCrew: number;
  byManufacturer: { [key: string]: number };
  byRole: { [key: string]: number };
  byStatus: { [key: string]: number };
}

const UserFleet: React.FC = () => {
  const { user } = useAuth();
  const [fleetShips, setFleetShips] = useState<UserFleetShip[]>([]);
  const [filteredShips, setFilteredShips] = useState<UserFleetShip[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadSource, setUploadSource] = useState<'hangarxplor' | 'fleetyards'>('hangarxplor');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterManufacturer, setFilterManufacturer] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAcquisition, setFilterAcquisition] = useState('all');
  const [filterPackage, setFilterPackage] = useState('all');
  const [filterPledge, setFilterPledge] = useState<'all' | 'pledged' | 'not-pledged'>('all');
  const [togglingPledgeId, setTogglingPledgeId] = useState<number | null>(null);
  
  // Sort states
  const [sortColumn, setSortColumn] = useState<'shipName' | 'manufacturer' | 'role' | 'status' | 'acquisitionMethod' | 'value' | 'cargoCapacity' | 'crew'>('shipName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Stats
  const [stats, setStats] = useState<FleetStats>({
    totalShips: 0,
    totalValue: 0,
    totalCargo: 0,
    totalCrew: 0,
    byManufacturer: {},
    byRole: {},
    byStatus: {}
  });

  useEffect(() => {
    fetchUserFleet();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [fleetShips, searchTerm, filterManufacturer, filterRole, filterStatus, filterAcquisition, filterPackage, filterPledge, sortColumn, sortDirection]);

  useEffect(() => {
    calculateStats();
  }, [fleetShips]);

  const fetchUserFleet = async () => {
    try {
      setLoading(true);
      const response = await api.get('/userfleet');
      setFleetShips(response.data);
    } catch (error) {
      console.error('Error fetching fleet:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: 'shipName' | 'manufacturer' | 'role' | 'status' | 'acquisitionMethod' | 'value' | 'cargoCapacity' | 'crew') => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const applyFilters = () => {
    let filtered = [...fleetShips];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(ship =>
        ship.shipName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ship.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ship.variant.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Manufacturer filter
    if (filterManufacturer !== 'all') {
      filtered = filtered.filter(ship => ship.manufacturer === filterManufacturer);
    }

    // Role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(ship => ship.role === filterRole);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(ship => ship.status === filterStatus);
    }

    // Acquisition filter
    if (filterAcquisition !== 'all') {
      filtered = filtered.filter(ship => ship.acquisitionMethod === filterAcquisition);
    }

    // Package filter
    if (filterPackage !== 'all') {
      filtered = filtered.filter(ship => ship.pledgeId === filterPackage);
    }

    // Org-pledge filter
    if (filterPledge === 'pledged') {
      filtered = filtered.filter(ship => !!ship.pledgedToOrg);
    } else if (filterPledge === 'not-pledged') {
      filtered = filtered.filter(ship => !ship.pledgedToOrg);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortColumn];
      let bValue: any = b[sortColumn];
      
      // Handle null/undefined values
      if (aValue == null) aValue = sortColumn === 'shipName' || sortColumn === 'manufacturer' || sortColumn === 'role' ? '' : 0;
      if (bValue == null) bValue = sortColumn === 'shipName' || sortColumn === 'manufacturer' || sortColumn === 'role' ? '' : 0;
      
      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Numeric comparison
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    setFilteredShips(filtered);
  };

  const calculateStats = () => {
    const newStats: FleetStats = {
      totalShips: fleetShips.length,
      totalValue: 0,
      totalCargo: 0,
      totalCrew: 0,
      byManufacturer: {},
      byRole: {},
      byStatus: {}
    };

    fleetShips.forEach(ship => {
      newStats.totalValue += ship.value || 0;
      newStats.totalCargo += ship.cargoCapacity || 0;
      newStats.totalCrew += ship.crew || 0;

      // Count by manufacturer
      newStats.byManufacturer[ship.manufacturer] = (newStats.byManufacturer[ship.manufacturer] || 0) + 1;

      // Count by role
      newStats.byRole[ship.role] = (newStats.byRole[ship.role] || 0) + 1;

      // Count by status
      newStats.byStatus[ship.status] = (newStats.byStatus[ship.status] || 0) + 1;
    });

    setStats(newStats);
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;

    try {
      const fileContent = await uploadFile.text();
      const jsonData = JSON.parse(fileContent);

      // Validate JSON structure
      if (!Array.isArray(jsonData)) {
        alert('Invalid JSON format. Expected an array of ships.');
        return;
      }

      // Upload to backend
      const endpoint = uploadSource === 'fleetyards'
        ? '/userfleet/import/fleetyards'
        : '/userfleet/import';
      const response = await api.post(endpoint, jsonData);

      if (response.status === 200) {
        const data = response.data;
        
        // Create detailed success/warning message
        const skipped = data.skipped ?? 0;
        const totalSubmitted = data.totalSubmitted ?? data.count;
        let message = `✅ Fleet imported successfully!\n\n`;
        message += `📊 Import Summary:\n`;
        message += `  • Ships in upload: ${totalSubmitted}\n`;
        message += `  • Newly added to fleet: ${data.count}\n`;
        message += `  • Successfully matched to DB: ${data.matched}\n`;

        if (skipped > 0) {
          message += `  • ⏭️ Skipped (already in your fleet): ${skipped}\n`;
        }

        if (data.unmatched > 0) {
          message += `  • ⚠️ Unmatched to DB: ${data.unmatched}\n\n`;
          message += `The following ships could not be automatically matched to the database:\n`;
          data.unmatchedShips.forEach((ship: any) => {
            const shipName = ship?.shipName ?? ship?.ShipName ?? 'Unknown';
            const manufacturer = ship?.manufacturer ?? ship?.Manufacturer ?? 'Unknown';
            message += `  • ${shipName} (${manufacturer})\n`;
          });
          message += `\n💡 These ships were added to your fleet but may lack images and stats.\n`;
          message += `Tip: Click "Rematch Ships" later — the DB may grow to include them.`;
        } else if (data.count > 0) {
          message += `\n🎉 All newly-added ships matched successfully!`;
        }

        if (skipped > 0 && data.count === 0) {
          message += `\nℹ️ Nothing new to add — all ships in the upload are already in your fleet.`;
        }
        
        alert(message);
        setShowUploadModal(false);
        setUploadFile(null);
        fetchUserFleet();
      } else {
        alert('Error importing fleet. Please check your file format.');
      }
    } catch (error: any) {
      console.error('Error uploading fleet:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error parsing JSON file';
      alert(`❌ Import Failed\n\n${errorMsg}\n\nPlease check your JSON format and try again.`);
    }
  };

  const openUploadModal = (source: 'hangarxplor' | 'fleetyards') => {
    setUploadSource(source);
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const [rematching, setRematching] = useState(false);

  const handleRematchFleet = async () => {
    if (rematching) return;
    try {
      setRematching(true);
      const response = await api.post('/userfleet/rematch');
      const { rematched, total, stillUnmatched } = response.data || {};
      let msg = `Rematch complete.\n\nUpdated: ${rematched ?? 0} of ${total ?? 0} ships.`;
      if (typeof stillUnmatched === 'number' && stillUnmatched > 0) {
        msg += `\nStill unmatched: ${stillUnmatched} (no DB match found).`;
      }
      alert(msg);
      fetchUserFleet();
    } catch (error: any) {
      console.error('Error rematching fleet:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Rematch failed';
      alert(`Rematch failed.\n\n${errorMsg}`);
    } finally {
      setRematching(false);
    }
  };

  const handleTogglePledge = async (ship: UserFleetShip) => {
    if (togglingPledgeId !== null) return;

    if (!ship.pledgedToOrg && !PLEDGE_ELIGIBLE_STATUSES.has(ship.status)) {
      alert(`Cannot pledge "${ship.shipName}" — only ships you own (status: owned or pledged) can be pledged to the org.\n\nCurrent status: ${ship.status}.`);
      return;
    }

    const goingOn = !ship.pledgedToOrg;
    try {
      setTogglingPledgeId(ship.id);
      // Optimistic update
      setFleetShips(prev =>
        prev.map(s => s.id === ship.id ? { ...s, pledgedToOrg: goingOn, pledgedAt: goingOn ? new Date().toISOString() : undefined } : s)
      );
      await api.put(`/userfleet/${ship.id}/pledge`, { pledged: goingOn });
    } catch (error: any) {
      console.error('Error toggling pledge:', error);
      const msg = error.response?.data?.message || error.message || 'Pledge update failed';
      alert(`Could not ${goingOn ? 'pledge' : 'unpledge'} this ship.\n\n${msg}`);
      // Roll back optimistic update
      setFleetShips(prev =>
        prev.map(s => s.id === ship.id ? { ...s, pledgedToOrg: !goingOn } : s)
      );
    } finally {
      setTogglingPledgeId(null);
    }
  };

  const handleDeleteEntireFleet = async () => {
    const pledgedCount = fleetShips.filter(s => s.pledgedToOrg).length;
    const pledgeWarning = pledgedCount > 0
      ? `\n\n⚠️ ${pledgedCount} of these ships ${pledgedCount === 1 ? 'is' : 'are'} pledged to the org and will disappear from the Org Fleet page. This will be logged in the org audit trail.`
      : '';
    if (!window.confirm(`Are you sure you want to delete your entire fleet? This will remove all ${fleetShips.length} ships and cannot be undone.${pledgeWarning}`)) {
      return;
    }

    try {
      const response = await api.delete('/userfleet');

      if (response.status === 200) {
        alert(`Successfully deleted ${response.data.count} ships from your fleet.`);
        fetchUserFleet();
      } else {
        alert('Error deleting fleet. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting fleet:', error);
      alert('Error deleting fleet. Please try again.');
    }
  };

  // Get unique values for filters
  const manufacturers = Array.from(new Set(fleetShips.map(ship => ship.manufacturer))).sort();
  const roles = Array.from(new Set(fleetShips.map(ship => ship.role))).sort();
  
  // Get unique packages with their names
  const uniquePackages = new Map<string, string>();
  fleetShips.forEach(ship => {
    if (ship.pledgeId && !uniquePackages.has(ship.pledgeId)) {
      uniquePackages.set(ship.pledgeId, ship.pledgeName || `Package ${ship.pledgeId.substring(0, 8)}`);
    }
  });
  const packages = Array.from(uniquePackages.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (loading) {
    return (
      <div className={styles.fleetContainer}>
        <div className={styles.loading}>
          <p>Loading your fleet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.fleetContainer}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <Rocket className={styles.titleIcon} /> My Fleet
          </h1>
          <p className={styles.subtitle}>Manage and track your Star Citizen ship collection</p>
        </div>
        <div className={styles.headerButtons}>
          <Link to="/fleet-import-guide" className={styles.guideButton}>
            <BookOpen size={16} /> Import Guide
          </Link>
          {fleetShips.length > 0 && (
            <>
              <button className={styles.uploadButton} onClick={() => openUploadModal('hangarxplor')}>
                <Upload size={16} /> Upload HangarXPLOR JSON
              </button>
              <button className={styles.uploadButton} onClick={() => openUploadModal('fleetyards')}>
                <Upload size={16} /> Upload FleetYards JSON
              </button>
              <button
                className={styles.guideButton}
                onClick={handleRematchFleet}
                disabled={rematching}
                title="Re-run ship matching to refresh images and stats from the database"
              >
                <RefreshCw size={16} className={rematching ? styles.spinning : undefined} />
                {rematching ? ' Rematching...' : ' Rematch Ships'}
              </button>
              <button className={styles.deleteAllButton} onClick={handleDeleteEntireFleet}>
                <Trash2 size={16} /> Delete Entire Fleet
              </button>
            </>
          )}
        </div>
      </div>

      {/* Fleet Statistics */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statContent}>
            <Rocket className={styles.statIcon} />
            <div>
              <div className={styles.statValue}>{stats.totalShips}</div>
              <div className={styles.statLabel}>Total Ships</div>
            </div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statContent}>
            <DollarSign className={styles.statIcon} />
            <div>
              <div className={styles.statValue}>${stats.totalValue.toLocaleString()}</div>
              <div className={styles.statLabel}>Total Value</div>
            </div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statContent}>
            <Package className={styles.statIcon} />
            <div>
              <div className={styles.statValue}>{stats.totalCargo.toLocaleString()} SCU</div>
              <div className={styles.statLabel}>Total Cargo</div>
            </div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statContent}>
            <Users className={styles.statIcon} />
            <div>
              <div className={styles.statValue}>{stats.totalCrew}</div>
              <div className={styles.statLabel}>Total Crew</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className={styles.filterCard}>
        <div className={styles.filterRow}>
          <div className={styles.searchBox}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search ships..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={filterManufacturer} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterManufacturer(e.target.value)}>
            <option value="all">All Manufacturers</option>
            {manufacturers.map(mfg => (
              <option key={mfg} value={mfg}>{mfg}</option>
            ))}
          </select>
          <select value={filterRole} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterRole(e.target.value)}>
            <option value="all">All Roles</option>
            {roles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="owned">Owned</option>
            <option value="pledged">Pledged</option>
            <option value="planned">Planned</option>
            <option value="rented">Rented</option>
            <option value="loaned">Loaned</option>
          </select>
          <select value={filterAcquisition} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterAcquisition(e.target.value)}>
            <option value="all">All Acquisition</option>
            <option value="pledge">Pledge</option>
            <option value="in-game">In-Game</option>
            <option value="upgrade">Upgrade</option>
            <option value="gift">Gift</option>
            <option value="rental">Rental</option>
            <option value="loaned">Loaned</option>
          </select>
          <select value={filterPackage} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterPackage(e.target.value)}>
            <option value="all">All Packages</option>
            {packages.map(pkg => (
              <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
            ))}
          </select>
          <select
            value={filterPledge}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterPledge(e.target.value as typeof filterPledge)}
            title="Filter by org pledge state"
          >
            <option value="all">All Ships</option>
            <option value="pledged">🚩 Pledged to Org</option>
            <option value="not-pledged">Not Pledged</option>
          </select>
          <div className={styles.viewToggle}>
            <button
              className={viewMode === 'grid' ? styles.active : ''}
              onClick={() => setViewMode('grid')}
            >
              <Grid3x3 size={16} />
            </button>
            <button
              className={viewMode === 'list' ? styles.active : ''}
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Fleet Display */}
      {filteredShips.length === 0 ? (
        <div className={styles.emptyState}>
          <Rocket size={64} />
          <h3>No ships in your fleet yet</h3>
          <p>Upload your fleet JSON to get started</p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className={styles.uploadButton} onClick={() => openUploadModal('hangarxplor')}>
              <Upload size={16} /> Upload HangarXPLOR JSON
            </button>
            <button className={styles.uploadButton} onClick={() => openUploadModal('fleetyards')}>
              <Upload size={16} /> Upload FleetYards JSON
            </button>
          </div>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className={styles.shipGrid}>
              {filteredShips.map(ship => {
                const pledgeEligible = PLEDGE_ELIGIBLE_STATUSES.has(ship.status);
                const isToggling = togglingPledgeId === ship.id;
                const pledgeBtnTitle = ship.pledgedToOrg
                  ? 'Pledged to org — click to withdraw'
                  : pledgeEligible
                    ? 'Pledge this ship for org use'
                    : `Cannot pledge: status is "${ship.status}" (only owned/pledged ships are eligible)`;
                return (
                  <div key={ship.id} className={`${styles.shipCard} ${ship.pledgedToOrg ? styles.shipCardPledged : ''}`}>
                    <div className={styles.shipImageContainer}>
                      {ship.imageUrl ? (
                        <img src={ship.imageUrl} alt={ship.shipName} className={styles.shipImage} />
                      ) : (
                        <div className={styles.shipImagePlaceholder} title={ship.shipId ? 'No image available' : 'Ship not matched to database'}>
                          <img src={tficLogo} alt="" className={styles.placeholderLogo} />
                          <span className={styles.placeholderLabel}>
                            {ship.shipId ? 'No image' : 'Unmatched'}
                          </span>
                        </div>
                      )}
                      {ship.pledgedToOrg && (
                        <div className={styles.pledgeRibbon} title="Pledged to TFIC org fleet">
                          <Flag size={12} /> ORG
                        </div>
                      )}
                    </div>
                    <div className={styles.shipBody}>
                      <h3 className={styles.shipName}>{ship.shipName}</h3>
                      <p className={styles.shipManufacturer}>{ship.manufacturer}</p>
                      <div className={styles.shipBadges}>
                        <span className={`${styles.badge} ${styles[`badge${ship.status}`]}`}>
                          {ship.status}
                        </span>
                        <span className={`${styles.badge} ${styles[`badge${ship.acquisitionMethod}`]}`}>
                          {ship.acquisitionMethod}
                        </span>
                      </div>
                      <div className={styles.shipDetails}>
                        <div className={styles.shipMeta}>
                          <span><strong>Role:</strong> {ship.role}</span>
                          {ship.value && <span><strong>Value:</strong> ${ship.value.toLocaleString()}</span>}
                          {ship.cargoCapacity && <span><strong>Cargo:</strong> {ship.cargoCapacity} SCU</span>}
                          {ship.crew && <span><strong>Crew:</strong> {ship.crew}</span>}
                        </div>
                        {ship.notes && (
                          <p className={styles.shipNotes}>{ship.notes}</p>
                        )}
                        <button
                          className={`${styles.pledgeButton} ${ship.pledgedToOrg ? styles.pledgeButtonActive : ''}`}
                          onClick={() => handleTogglePledge(ship)}
                          disabled={isToggling || (!ship.pledgedToOrg && !pledgeEligible)}
                          title={pledgeBtnTitle}
                        >
                          <Flag size={14} />
                          {isToggling
                            ? ' Updating...'
                            : ship.pledgedToOrg
                              ? ' Pledged to Org'
                              : ' Pledge to Org'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.shipTable}>
              <table>
                <thead>
                  <tr>
                    <th className={styles.sortableHeader} onClick={() => handleSort('shipName')}>
                      Ship {sortColumn === 'shipName' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className={styles.sortableHeader} onClick={() => handleSort('manufacturer')}>
                      Manufacturer {sortColumn === 'manufacturer' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className={styles.sortableHeader} onClick={() => handleSort('role')}>
                      Role {sortColumn === 'role' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className={styles.sortableHeader} onClick={() => handleSort('status')}>
                      Status {sortColumn === 'status' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className={styles.sortableHeader} onClick={() => handleSort('acquisitionMethod')}>
                      Acquisition {sortColumn === 'acquisitionMethod' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className={styles.sortableHeader} onClick={() => handleSort('value')}>
                      Value {sortColumn === 'value' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className={styles.sortableHeader} onClick={() => handleSort('cargoCapacity')}>
                      Cargo {sortColumn === 'cargoCapacity' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className={styles.sortableHeader} onClick={() => handleSort('crew')}>
                      Crew {sortColumn === 'crew' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredShips.map(ship => (
                    <tr key={ship.id}>
                      <td><strong>{ship.shipName}</strong></td>
                      <td>{ship.manufacturer}</td>
                      <td>{ship.role}</td>
                      <td>
                        <span className={`${styles.badge} ${styles[`badge${ship.status}`]}`}>
                          {ship.status}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.badge} ${styles[`badge${ship.acquisitionMethod}`]}`}>
                          {ship.acquisitionMethod}
                        </span>
                      </td>
                      <td>${ship.value?.toLocaleString() || '-'}</td>
                      <td>{ship.cargoCapacity || '-'} SCU</td>
                      <td>{ship.crew || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className={styles.modalOverlay} onClick={() => setShowUploadModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>
                Upload Fleet JSON ({uploadSource === 'fleetyards' ? 'FleetYards' : 'HangarXPLOR'})
              </h2>
              <button onClick={() => setShowUploadModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Select Fleet JSON File</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    if (file) setUploadFile(file);
                  }}
                />
                <small>
                  Upload a JSON file containing your fleet data. 
                  See the <Link to="/fleet-import-guide" className={styles.guideLink}>Import Guide</Link> for detailed format documentation.
                </small>
              </div>
              <div className={styles.jsonExample}>
                <h6>Minimal Example:</h6>
                <pre>
{uploadSource === 'fleetyards'
  ? `[
  {
    "name": "600i Explorer",
    "manufacturerName": "Origin Jumpworks"
  }
]`
  : `[
  {
    "shipName": "Anvil Hornet F7C",
    "manufacturer": "Anvil Aerospace"
  }
]`}
                </pre>
                <small style={{ color: '#adb5bd', display: 'block', marginTop: '0.5rem' }}>
                  {uploadSource === 'fleetyards'
                    ? '💡 FleetYards hangar export format. Check the Import Guide for details.'
                    : '💡 Supports HangarXPLOR format automatically. Check the Import Guide for complete field list.'}
                </small>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={() => setShowUploadModal(false)}>
                Cancel
              </button>
              <button className={styles.uploadButton} onClick={handleFileUpload} disabled={!uploadFile}>
                Upload Fleet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFleet;
